import { useParams, useSearchParams } from "#src/Routers/hooks";
import { CreateWorkflowDialog } from "#src/batteries-included-components/Dialogs/CreateWorkflowDialog";
import { EditWorkflowDialog } from "#src/batteries-included-components/Dialogs/EditWorkflowDialog";
import {
  WorkflowGraphCanvas,
  WorkflowGraphCanvasOuterBoundaryId,
} from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphCanvas";
import { useWorkflowGraphState } from "#src/batteries-included-components/Graph/WorkflowGraph/WorkflowGraphCanvas/WorkflowGraphStore";
import { WorkflowsListLayout } from "#src/batteries-included-components/Layouts/Workflows/List";
import { useGetOneWorkflowTemplate } from "#src/components/hooks/adapters/useWorkflowTemplates";
import { useActionDetails } from "#src/components/hooks/useActionDetails";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { WorkflowsRoutePath } from "#src/routes/workflows";
import { WorkflowCategoriesRoutePath } from "#src/routes/workflows/categories";
import { WorkflowCategoryDetailsRoutePath } from "#src/routes/workflows/categories/[workflowCategoryId]";
import { displayLocalizedAssetType } from "#src/utils/display";
import { RoutePath, useBreadcrumbsFromRoutes } from "#src/utils/route";
import { WorkflowTemplateStatusToPillVariantMapping } from "#src/view-models/workflows";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Page,
  PageFullScreenContainer,
  Pill,
  Tab,
  generateDocumentTitle,
} from "@validereinc/common-components";
import { ResourceDefinitions, UsersAdapter } from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useState } from "react";
import { Titled } from "react-titled";
import { WorkflowTemplateDetailsRoutePath } from ".";
import { WorkflowTemplateAssetTable } from "./WorkflowTemplateAssetTable";
import { WorkflowTemplateKeyValuePanel } from "./WorkflowTemplateKeyValuePanel";
import styles from "./WorkflowTemplatePage.module.scss";

const cx = classNames.bind(styles);

const DEFAULT_TAB_KEY = "overview";

export const WorkflowTemplatePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [templateIdForDialogOpen, setTemplateIdForDialogOpen] =
    useState<string>("");
  const [isEditingTemplate, setIsEditingTemplate] = useState<boolean>(false);

  const [
    filterConfigStorageKey,
    tableConfigStorageKey,
    viewConfigStorageKey,
    tableStateStorageKey,
    groupByTablesStateStorageKey,
  ] = useStorageKey(
    [
      "filter-configuration",
      "table-configuration",
      "view-configuration",
      "table-state",
      "group-by-tables-state",
    ],
    "workflows-template"
  );
  const { workflowTemplateId } = useParams<{
    workflowTemplateId: string;
  }>();

  const { data, isLoading } = useGetOneWorkflowTemplate({
    id: workflowTemplateId,
  });

  const [canEditWorkflowTemplate] = useIsFeatureAvailable({
    featureFlagQuery: "core:workflows",
    permissionQuery: "workflow_templates:write",
  });
  const [isVisualizationFeatureAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "workflows:visualization",
  });

  const templateDetails = data?.data;

  const editInformation = useActionDetails({
    type: "edit",
    date: templateDetails?.updated_at,
    byUserId: templateDetails?.updated_by,
  });

  const { data: ownerUser } = useQuery(
    ["users", templateDetails?.workflow_owner_id],
    async () => {
      if (!templateDetails?.workflow_owner_id) {
        return;
      }

      return UsersAdapter.getOne({
        id: templateDetails?.workflow_owner_id,
      });
    },
    {
      enabled: Boolean(templateDetails?.workflow_owner_id),
      staleTime: 5 * 60 * 1000,
      select: (resp) => resp?.data,
    }
  );

  const [breadcrumbs] = useBreadcrumbsFromRoutes(
    [
      [WorkflowsRoutePath, {}],
      [WorkflowCategoriesRoutePath, {}],
      [
        WorkflowCategoryDetailsRoutePath,
        {
          title: templateDetails?.workflow_category.name,
          pathParams: {
            workflowCategoryId: templateDetails?.workflow_category.id ?? "",
          },
        },
      ],
      [
        new RoutePath({
          path: "",
          isPresentational: true,
          title: "Workflow Templates",
        }),
        {},
      ],
      [
        WorkflowTemplateDetailsRoutePath,
        {
          title: templateDetails?.name,
        },
      ],
    ],
    !!templateDetails
  );

  const onOpenCreateWorkflowDialog = () =>
    setTemplateIdForDialogOpen(workflowTemplateId);

  const onCloseCreateWorkflowDialog = () => setTemplateIdForDialogOpen("");

  const localizedAssetType = displayLocalizedAssetType(
    templateDetails?.asset_type
  );

  const { selectedNode } = useWorkflowGraphState();

  return (
    <>
      <Page
        isLoading={isLoading}
        category={WorkflowTemplateDetailsRoutePath.previous?.title}
        title={
          templateDetails ? (
            <>
              {templateDetails.name}&nbsp;
              <Pill
                variant={
                  WorkflowTemplateStatusToPillVariantMapping[
                    templateDetails.status
                  ] ?? "default"
                }
                hasDot={false}
              >
                {toTitleCaseString(templateDetails.status, {
                  intent: "ui",
                  exceptions: ["oil-and-gas"],
                })}
              </Pill>
            </>
          ) : (
            WorkflowTemplateDetailsRoutePath.title
          )
        }
        breadcrumbs={breadcrumbs}
        actionRow={[
          ...(canEditWorkflowTemplate
            ? [
                <Button
                  key="edit-workflow-action"
                  variant="outline"
                  disabled={isLoading}
                  onClick={() => setIsEditingTemplate(true)}
                >
                  Edit
                </Button>,
              ]
            : []),
          <Button
            key="create-workflow-action"
            variant="primary"
            onClick={onOpenCreateWorkflowDialog}
          >
            Create Workflow
          </Button>,
        ]}
        renderMeta={
          !editInformation.description
            ? undefined
            : ({ MetaSegments }) => (
                <MetaSegments values={[editInformation.description]} />
              )
        }
        activeTabKey={searchParams?.tab ?? DEFAULT_TAB_KEY}
        onActiveTabKeyChange={(tabKey: string) => {
          setSearchParams({ tab: tabKey });
        }}
      >
        <Tab
          title="Overview"
          tabKey="overview"
        >
          <Titled
            title={generateDocumentTitle(
              templateDetails?.name
                ? templateDetails.name
                : WorkflowTemplateDetailsRoutePath.title,
              ResourceDefinitions.workflow_template.label.plural
            )}
          >
            <div>
              <WorkflowTemplateKeyValuePanel
                templateDetails={templateDetails}
                isLoading={isLoading}
                workflowOwnerName={ownerUser?.name}
              />
              <div className={cx("allWorkflowsContainer")}>
                <WorkflowsListLayout
                  tablePanelProps={{
                    filterConfigStorageKey,
                    tableConfigStorageKey,
                    tableStateStorageKey,
                    viewConfigStorageKey,
                    groupByTablesStateStorageKey,
                    isWithinWorkflowTemplateDetails: true,
                    overrideFilters: {
                      templateId: templateDetails?.id,
                    },
                  }}
                  defaultAssetType={templateDetails?.asset_type}
                />
              </div>
            </div>
          </Titled>
        </Tab>
        {templateDetails?.asset_type ? (
          <Tab
            title={localizedAssetType}
            tabKey={localizedAssetType.toLowerCase()}
          >
            <WorkflowTemplateAssetTable
              assetType={templateDetails?.asset_type}
              assetsQueryFilters={templateDetails?.asset_filter}
            />
          </Tab>
        ) : null}
        {isVisualizationFeatureAvailable && (
          <Tab
            title="Configuration"
            tabKey="configuration"
          >
            <PageFullScreenContainer
              id={WorkflowGraphCanvasOuterBoundaryId}
              offsetRight={selectedNode ? 480 : 0}
            >
              <WorkflowGraphCanvas
                templateId={workflowTemplateId}
                assetType={templateDetails?.asset_type}
                mode="workflow-template"
              />
            </PageFullScreenContainer>
          </Tab>
        )}
      </Page>
      <CreateWorkflowDialog
        onClose={onCloseCreateWorkflowDialog}
        templateId={templateIdForDialogOpen}
      />
      {canEditWorkflowTemplate && templateDetails && (
        <EditWorkflowDialog
          onClose={() => setIsEditingTemplate(false)}
          template={templateDetails}
          isOpen={isEditingTemplate}
        />
      )}
    </>
  );
};
